import { Component } from '@angular/core';
import { MainLayout } from '../../layouts/main-layout';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'allas-rastrear-pedido',
  imports: [MainLayout, ReactiveFormsModule, CommonModule],
  template: `
    <allas-main-layout [showPopularSections]="true" [showNewsletter]="true">
      <div class="min-h-screen bg-white">
        <!-- Main Content -->
        <section class="mx-auto my-12 max-w-2xl px-6 sm:px-8">
          <div class="text-center mb-12">
            <h1 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              RASTREAR MI PEDIDO
            </h1>
            <p class="text-lg text-gray-600">
              Encuentra tu pedido con el formulario de abajo.
            </p>
          </div>

          <!-- Tracking Form -->
          <div class="bg-white rounded-lg">
            <form
              [formGroup]="trackingForm"
              (ngSubmit)="onSubmit()"
              class="space-y-6"
            >
              <!-- Form Fields -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Email Field -->
                <div class="space-y-2">
                  <input
                    type="email"
                    formControlName="email"
                    placeholder="Correo Electrónico *"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    [class.border-red-500]="isFieldInvalid('email')"
                  />
                  <div
                    *ngIf="isFieldInvalid('email')"
                    class="text-red-500 text-sm"
                  >
                    {{ getFieldError('email') }}
                  </div>
                </div>

                <!-- Phone Field -->
                <div class="space-y-2">
                  <input
                    type="tel"
                    formControlName="phone"
                    placeholder="Número de Teléfono *"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    [class.border-red-500]="isFieldInvalid('phone')"
                  />
                  <div
                    *ngIf="isFieldInvalid('phone')"
                    class="text-red-500 text-sm"
                  >
                    {{ getFieldError('phone') }}
                  </div>
                </div>
              </div>

              <!-- Invoice Number Field -->
              <div class="space-y-2">
                <input
                  type="text"
                  formControlName="invoiceNumber"
                  placeholder="Número de Factura *"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  [class.border-red-500]="isFieldInvalid('invoiceNumber')"
                />
                <div
                  *ngIf="isFieldInvalid('invoiceNumber')"
                  class="text-red-500 text-sm"
                >
                  {{ getFieldError('invoiceNumber') }}
                </div>
              </div>

              <!-- Submit Button -->
              <div class="flex justify-center">
                <button
                  type="submit"
                  [disabled]="trackingForm.invalid || isSubmitting"
                  class="px-8 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ isSubmitting ? 'BUSCANDO...' : 'BUSCAR PEDIDO' }}
                </button>
              </div>
            </form>
          </div>

          <!-- Additional Information -->
          <div class="mt-8 text-center">
            <p class="text-sm text-gray-500">
              ¿No tienes tu número de factura?
              <a
                href="/contactanos"
                class="text-blue-600 hover:text-blue-800 underline"
              >
                Contáctanos
              </a>
            </p>
          </div>
        </section>
      </div>
    </allas-main-layout>
  `,
})
export class RastrearPedido {
  trackingForm: FormGroup;
  isSubmitting = false;

  constructor(private fb: FormBuilder) {
    this.trackingForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      phone: [
        '',
        [Validators.required, Validators.pattern(/^[\+]?[1-9][\d]{0,15}$/)],
      ],
      invoiceNumber: ['', [Validators.required, Validators.minLength(3)]],
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.trackingForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.trackingForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) {
      return `${this.getFieldDisplayName(fieldName)} es requerido`;
    }
    if (field.errors['email']) {
      return 'Correo electrónico inválido';
    }
    if (field.errors['pattern']) {
      return 'Número de teléfono inválido';
    }
    if (field.errors['minlength']) {
      return 'Número de factura debe tener al menos 3 caracteres';
    }

    return 'Campo inválido';
  }

  getFieldDisplayName(fieldName: string): string {
    const names: { [key: string]: string } = {
      email: 'Correo electrónico',
      phone: 'Número de teléfono',
      invoiceNumber: 'Número de factura',
    };
    return names[fieldName] || fieldName;
  }

  onSubmit() {
    if (this.trackingForm.valid) {
      this.isSubmitting = true;

      // Simular búsqueda de pedido
      setTimeout(() => {
        this.isSubmitting = false;

        // Aquí iría la lógica real para buscar el pedido
        // Por ahora solo mostramos un mensaje de ejemplo
        alert('Función de búsqueda de pedido en desarrollo');
      }, 1000);
    } else {
      // Marcar todos los campos como touched para mostrar errores
      Object.keys(this.trackingForm.controls).forEach((key) => {
        const control = this.trackingForm.get(key);
        control?.markAsTouched();
      });
    }
  }
}
